import { FormControl, FormGroup } from "@angular/forms";

export interface Client {
  email?: string;
  domain: string;
  createdAt: number;
  planId?: string;
  stripeId?: string;
  googleAdminEmail?: string;
  trialFinishDate?: Date;
}

export interface AddClientRequest {
  domain: string;
  googleAdminEmail: string;
  isIncludedDemoContent: boolean;
  trialFinishDate: Date | null;
  planId: ClientPlanId;
}

export const CLIENT_PLAN_OPTIONS = [
  "free",
  "freemium",
  "paid",
  "partner",
  "reseller",
] as const;

export type ClientPlanId = (typeof CLIENT_PLAN_OPTIONS)[number];

export const CLIENT_PLAN_NAMES: Record<ClientPlanId, string> = {
  free: "basic",
  freemium: "basic",
  paid: "premium",
  partner: "partner",
  reseller: "reseller",
};

export const LINK_PREVIEW_MODES = [
  "photo_name_title",
  "name_title",
  "org_branding",
  "disabled",
] as const;

export type LinkPreviewMode = (typeof LINK_PREVIEW_MODES)[number];

export const NAME_PRONUNCIATION_SCOPES = [
  "all_users",
  "designated_roles",
] as const;

export type NamePronunciationScope = (typeof NAME_PRONUNCIATION_SCOPES)[number];

export const NAME_PRONUNCIATION_ROLE_OPTIONS = [
  "SUPER_ADMIN",
  "RESELLER",
  "GROUP_MANAGER",
  "COMPANY_MANAGER",
] as const;

export type NamePronunciationRole =
  (typeof NAME_PRONUNCIATION_ROLE_OPTIONS)[number];

export const BILLING_MODES = ["default", "legacy", "upfront"] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export interface OptionalInterfaces {
  digitalCard: boolean;
  mobileApps: boolean;
  browserExtension: boolean;
  appleWallet: boolean;
  googleWallet: boolean;
  emailSignatures: boolean;
  virtualBackgrounds: boolean;
  linkPreviewMode: LinkPreviewMode;
  selfPrintCards: boolean;
  contextualQr: boolean;
  namePronunciation: boolean;
  namePronunciationScope: NamePronunciationScope;
  namePronunciationRoles: NamePronunciationRole[];
}

export const DEFAULT_OPTIONAL_INTERFACES: OptionalInterfaces = {
  digitalCard: false,
  mobileApps: false,
  browserExtension: false,
  appleWallet: false,
  googleWallet: false,
  emailSignatures: false,
  virtualBackgrounds: false,
  linkPreviewMode: "disabled",
  selfPrintCards: false,
  contextualQr: false,
  namePronunciation: false,
  namePronunciationScope: "all_users",
  namePronunciationRoles: [],
};

export interface OptionalInterfacesForm {
  digitalCard: FormControl<boolean>;
  mobileApps: FormControl<boolean>;
  browserExtension: FormControl<boolean>;
  appleWallet: FormControl<boolean>;
  googleWallet: FormControl<boolean>;
  emailSignatures: FormControl<boolean>;
  virtualBackgrounds: FormControl<boolean>;
  linkPreviewMode: FormControl<LinkPreviewMode>;
  selfPrintCards: FormControl<boolean>;
  contextualQr: FormControl<boolean>;
  namePronunciation: FormControl<boolean>;
  namePronunciationScope: FormControl<NamePronunciationScope>;
  namePronunciationRoles: FormControl<NamePronunciationRole[]>;
}

export interface EditClientForm {
  domain: FormControl<string>;
  domainChangeAcknowledged: FormControl<boolean>;
  googleAdminEmail: FormControl<string | null>;
  planId: FormControl<ClientPlanId | null>;
  trialFinishDate: FormControl<Date | null>;
  excludedNewEmployees: FormControl<boolean>;
  language: FormControl<string>;
  billingMode: FormControl<BillingMode>;
  optionalInterfaces: FormGroup<OptionalInterfacesForm>;
}

export interface AddClientForm {
  domain: FormControl<string | null>;
  googleAdminEmail: FormControl<string | null>;
  planId: FormControl<ClientPlanId>;
  isIncludedDemoContent: FormControl<boolean | null>;
  trialFinishDate: FormControl<Date | null>;
}

export interface EditClientRequest extends AddClientRequest {
  excludedNewEmployees?: boolean;
  language?: string;
  billingMode?: BillingMode;
  optionalInterfaces?: OptionalInterfaces;
}

export interface EditClientDialogData {
  domain: string;
  clientId?: string;
  googleAdminEmail?: string | null;
  planId?: string | null;
  trialFinishDate?: Date | null;
  excludedNewEmployees?: boolean | null;
  language?: string | null;
  billingMode?: BillingMode | null;
  optionalInterfaces?: OptionalInterfaces | null;
}

export interface ClientNote {
  clientNoteId: string;
  clientId: string;
  body: string;
  createdBy: string | null;
  createdAt: string | Date;
  modifiedAt: string | Date;
}

export interface ClientDocumentMeta {
  clientDocumentId: string;
  clientId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  createdAt: string | Date;
}

export const ALLOWED_CLIENT_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/zip",
] as const;

export interface ClientDocumentAddDialogData {
  clientId: string;
}

export interface ClientWithStats {
  clientId: string;
  domain: string;
  createdAt: Date;
  googleAdminEmail?: string | null;
  name?: string | null;
  website?: string | null;
  address?: string | null;
  phone?: string | null;
  photoUrl?: string | null;
  planId?: string | null;
  stats: {
    current: {
      numberOfEmployees: number;
      numberOfEmployeesWithSignatureInstalled: number;
    };
    previous: {
      numberOfEmployees: number;
      numberOfEmployeesWithSignatureInstalled: number;
    };
  };
  trialFinishDate?: Date | null;
  language?: string | null;
  billingMode?: BillingMode | null;
  parentDomain?: string | null;
  subdomains?: string[];
  excludedNewEmployees?: boolean | null;
  optionalInterfaces?: OptionalInterfaces | null;
  notes?: ClientNote[];
  documents?: ClientDocumentMeta[];
}

export interface ClientMonthlyStats {
  domain: string;
  date: Date;
  numberOfEmployees: number;
  price: number;
  currency: string;
}

export interface AddSubdomainDialogData {
  clientId: string;
  domain: string;
  planId?: string;
}

export interface AddSubdomainRequest {
  subdomain: string;
  googleAdminEmail: string;
  trialFinishDate?: Date;
}
